import { type MouseEvent, useMemo } from "react";
import { useParams } from "react-router";
import { Tooltip } from "@mui/material";
import type { EnhancedRun } from "src/business-components/input/PitSelector/PitSelector.types";
import Pit from "src/business-components/Pit/Pit";
import PipelinePitSelectorSelector from "src/business-components/pipeline/PipelinePitSelector/components/PipelinePitSelectorSelector/PipelinePitSelectorSelector";
import { usePipelinePitSelector } from "src/business-components/pipeline/PipelinePitSelector/PipelinePitSelector.hooks";
import PopperDropdown from "src/components/general/PopperDropdown/PopperDropdown";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useGlobalLocalContext } from "src/storage/localStorage";
import type { PitPath, TaskRunPath } from "src/utils/appRoutes/appRoutes.types";
import { CopyText } from "@components/features/small/CopyText/CopyText";
import { PitIcon } from "@ui-icons";
import BreadcrumbLink from "../BreadcrumbLink/BreadcrumbLink";
import BreadcrumbPopperArrow from "../BreadcrumbPopperArrow/BreadcrumbPopperArrow";

type LiMouse = MouseEvent<HTMLLIElement>;
interface BreadcrumbPitLinkProps {
  env?: string;
  app_name?: string;
  app_pit?: string;
  task_name?: string;
  isLoading?: boolean;
}

const BreadcrumbPitLink = ({
  app_name,
  task_name,
  isLoading,
}: BreadcrumbPitLinkProps) => {
  const { app_pit, task_id, tf_id: isTfPage } = useParams();
  const { isDebugMode } = useGlobalLocalContext();
  const buildLink = useBuildLink();

  const { redirectWithoutPIT, onClickRenderOption, appPits } =
    usePipelinePitSelector(task_name);

  const pitStatus = useMemo(() => {
    if (appPits?.length && app_pit) {
      return appPits.find((pit) => pit.app_pit === app_pit)?.status;
    }
  }, [app_pit, appPits]);

  const arrowMode = !!app_pit;

  return (
    <div className="flex items-center gap-1">
      <PopperDropdown
        slotProps={{
          popper: {
            disablePortal: true,
          },
        }}
        content={({ hide }) => (
          <PipelinePitSelectorSelector
            pit={app_pit}
            pits={appPits}
            isLoading={isLoading}
            onChange={(_e: LiMouse, option) => {
              if (!option.app_pit) {
                hide();
                redirectWithoutPIT();
              }
            }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: "200px",
                },
              },
              renderOption: {
                onClick(e: LiMouse, pit: EnhancedRun) {
                  hide();
                  onClickRenderOption(e, pit);
                },
              },
            }}
          />
        )}
      >
        {({ isOpen, anchorRef, handleOnMouseDownCapture, toggleAndPrevent }) => (
          <BreadcrumbLink
            ref={anchorRef}
            data-test-id="breadcrumb-pit"
            className="text-xs"
            to={
              (app_name &&
                app_pit &&
                buildLink({ app_name, app_pit, ...(isTfPage && { task_id }) } as
                  | TaskRunPath
                  | PitPath)) ||
              ""
            }
            startAdornment={PitIcon}
            onClick={arrowMode ? undefined : toggleAndPrevent}
            onMouseDownCapture={arrowMode ? undefined : handleOnMouseDownCapture}
            endAdornment={
              <BreadcrumbPopperArrow
                isOpen={isOpen}
                onClick={toggleAndPrevent}
                onMouseDownCapture={handleOnMouseDownCapture}
              />
            }
            TooltipComponent={Tooltip}
          >
            {app_pit ? (
              <>
                <Pit
                  basic
                  app_pit={app_pit}
                  status={pitStatus}
                  className="truncate"
                />
                {isDebugMode && <CopyText text={app_pit} hideText />}
              </>
            ) : (
              "Select PIT"
            )}
          </BreadcrumbLink>
        )}
      </PopperDropdown>
    </div>
  );
};

export default BreadcrumbPitLink;
